import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { API_URL } from '../config';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import { FaTruck, FaSpinner, FaMapMarkerAlt } from 'react-icons/fa';

const MAP_STYLES = { height: '60vh', width: '100%' };
const JHB = { lat: -26.2041, lng: 28.0473 };

// Role-based live map.
// filterBy 'subbie' + ownerId = only trucks owned by that subcontractor.
// filterBy 'client' + ownerId = only trucks hauling loads for that client.
function RoleLiveMap({ filterBy, ownerId, title, subtitle }) {
  const { isLoaded } = useGoogleMaps();
  const [allLocations, setAllLocations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const mapRef = useRef(null);

  const fetchLocations = useCallback(async () => {
    // Staff read the full feed; clients read their own scoped feed
    // (/api/client/locations). Everyone else gets the empty state.
    const role = JSON.parse(localStorage.getItem('user') || '{}').role;
    const isStaff = role === 'admin' || role === 'super_admin';
    const isClient = role === 'client';
    if (!isStaff && !isClient) {
      setAllLocations([]);
      setLoading(false);
      return;
    }
    try {
      const endpoint = isClient ? `${API_URL}/client/locations` : `${API_URL}/driver-app/locations`;
      const res = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (data.success) setAllLocations(data.locations || []);
    } catch (e) {
      console.error('❌ Role live map fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
    const id = setInterval(fetchLocations, 15000);
    return () => clearInterval(id);
  }, [fetchLocations]);

  useEffect(() => {
    if (!ownerId) { setLocations([]); return; }
    if (filterBy === 'subbie') {
      const filtered = allLocations.filter(loc => {
        const truckOwner = loc.trucks?.subcontractor_id;
        if (truckOwner && String(truckOwner) === String(ownerId)) return true;
        const loadOwner = loc.loads?.subcontractor_id;
        return loadOwner && String(loadOwner) === String(ownerId);
      });
      setLocations(filtered);
    } else if (filterBy === 'client') {
      const filtered = allLocations.filter(loc => {
        const loadClient = loc.loads?.client_id;
        return loadClient && String(loadClient) === String(ownerId);
      });
      setLocations(filtered);
    } else {
      setLocations(allLocations);
    }
    setSelected(null);
  }, [allLocations, filterBy, ownerId]);

  const onLoad = useCallback((map) => { mapRef.current = map; }, []);
  const onUnmount = useCallback(() => { mapRef.current = null; }, []);

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy">🗺 {title || 'Live Map'}</h1>
          <p className="text-gray-500 text-sm">{subtitle || 'Real-time truck locations (auto-refreshes)'}</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 px-4 py-2">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
          <span className="text-xs font-medium text-gray-600">{locations.length} active truck{locations.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading || !isLoaded ? (
          <div className="flex items-center justify-center" style={MAP_STYLES}>
            <div className="text-center">
              <FaSpinner className="animate-spin text-3xl text-green-500 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">Loading live map...</p>
            </div>
          </div>
        ) : (
          <GoogleMap mapContainerStyle={MAP_STYLES} center={JHB} zoom={6} onLoad={onLoad} onUnmount={onUnmount}>
            {locations.map((loc) => (
              <Marker key={loc.truck_id} position={{ lat: parseFloat(loc.lat), lng: parseFloat(loc.lng) }} onClick={() => setSelected(loc)} icon={{ url: 'https://maps.google.com/mapfiles/kml/shapes/truck.png', scaledSize: { width: 28, height: 28 } }} />
            ))}
            {selected && (
              <InfoWindow position={{ lat: parseFloat(selected.lat), lng: parseFloat(selected.lng) }} onCloseClick={() => setSelected(null)}>
                <div className="p-2 max-w-[220px]">
                  <p className="font-bold text-navy flex items-center gap-1"><FaTruck size={12} /> {selected.trucks?.unit_number || selected.trucks?.registration_number || 'Truck'}</p>
                  <p className="text-xs text-gray-600">{selected.drivers?.full_name || 'Unknown driver'} · {selected.drivers?.phone || ''}</p>
                  {selected.loads && <p className="text-xs text-gray-600 mt-1">{selected.loads.load_number} · {selected.loads.origin} → {selected.loads.destination}</p>}
                  {selected.loads?.clients?.company_name && <p className="text-xs text-gray-500 mt-1">Customer: {selected.loads.clients.company_name}</p>}
                  {selected.trucks?.subcontractors?.company_name && <p className="text-xs text-gray-500 mt-1">Subbie: {selected.trucks.subcontractors.company_name}</p>}
                  <p className="text-xs font-semibold mt-1" style={{ color: '#00A86B' }}>{selected.loads?.status || 'Available'}</p>
                  <p className="text-[10px] text-gray-400 mt-1"><FaMapMarkerAlt size={9} className="inline mr-1" />{parseFloat(selected.lat).toFixed(5)}, {parseFloat(selected.lng).toFixed(5)}</p>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        )}
      </div>

      <div className="mt-4 bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="font-semibold text-navy text-sm">Active Trucks</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {locations.length === 0 ? (
                <tr><td className="px-6 py-8 text-center text-gray-400">No active trucks match your filter yet.</td></tr>
              ) : locations.map((loc) => (
                <tr key={loc.truck_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-6 py-2 font-medium text-navy">{loc.trucks?.unit_number || loc.trucks?.registration_number || '-'}</td>
                  <td className="px-6 py-2">{loc.drivers?.full_name || '-'}</td>
                  <td className="px-6 py-2">{loc.loads?.load_number || '-'}</td>
                  <td className="px-6 py-2 text-gray-600">{loc.loads ? `${loc.loads.origin} → ${loc.loads.destination}` : '-'}</td>
                  <td className="px-6 py-2">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">{loc.loads?.status || 'Available'}</span>
                  </td>
                  <td className="px-6 py-2 text-gray-500 text-xs">{loc.last_ping_at ? new Date(loc.last_ping_at).toLocaleTimeString('en-ZA') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default RoleLiveMap;